// src/components/MemeEditor.tsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMemeTemplates } from "../context/MemeTemplateContext";
import { TextElement, MemeTemplate } from "../types";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Slider,
  Grid,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  AspectRatio as AspectRatioIcon,
} from "@mui/icons-material";

// Import fabric as a namespace
import * as fabric from "fabric";

// Add custom properties to fabric objects for TypeScript
declare module "fabric" {
  namespace fabric {
    interface FabricObject {
      data?: {
        id: string;
        [key: string]: any;
      };
    }

    interface IText {
      data?: {
        id: string;
        [key: string]: any;
      };
    }

    interface Canvas {
      backgroundImage: any;
      setBackgroundImage(image: any, callback: Function): void;
    }
  }
}

// Maximum canvas dimensions to prevent layout issues
const MAX_CANVAS_WIDTH = 800;
const MAX_CANVAS_HEIGHT = 800;

const MemeEditor = () => {
  const { id } = useParams<{ id: string }>();
  const { templates, loading } = useMemeTemplates();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);

  const [template, setTemplate] = useState<MemeTemplate | null>(null);
  const [textElements, setTextElements] = useState<TextElement[]>([]);
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null);
  const [canvasReady, setCanvasReady] = useState(false);
  const [canvasScale, setCanvasScale] = useState(1);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });
  const [highQualityExport, setHighQualityExport] = useState(true);

  // Calculate appropriate canvas dimensions based on template and screen size
  const calculateCanvasDimensions = useCallback(
    (templateWidth: number, templateHeight: number) => {
      let canvasWidth = templateWidth;
      let canvasHeight = templateHeight;
      let scale = 1;

      // Adjust maximum width for mobile
      const maxWidth = isMobile
        ? Math.min(window.innerWidth - 32, MAX_CANVAS_WIDTH)
        : MAX_CANVAS_WIDTH;
      const maxHeight = MAX_CANVAS_HEIGHT;

      // If template is larger than our max dimensions, scale it down proportionally
      if (canvasWidth > maxWidth || canvasHeight > maxHeight) {
        const widthRatio = maxWidth / canvasWidth;
        const heightRatio = maxHeight / canvasHeight;

        // Use the smaller ratio to ensure both dimensions fit
        scale = Math.min(widthRatio, heightRatio);

        // Apply scale to dimensions and ensure they're integers
        canvasWidth = Math.floor(canvasWidth * scale);
        canvasHeight = Math.floor(canvasHeight * scale);
      }

      return { width: canvasWidth, height: canvasHeight, scale };
    },
    [isMobile],
  );

  // Find the template
  useEffect(() => {
    if (!loading && templates.length > 0 && id) {
      const foundTemplate = templates.find((t) => t.id === id);
      if (foundTemplate) {
        setTemplate(foundTemplate);

        // Calculate dimensions for the canvas
        const { width, height, scale } = calculateCanvasDimensions(
          foundTemplate.width,
          foundTemplate.height,
        );
        setCanvasDimensions({ width, height });
        setCanvasScale(scale);
      } else {
        navigate("/");
      }
    }
  }, [id, templates, loading, navigate, calculateCanvasDimensions]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (template) {
        const { width, height, scale } = calculateCanvasDimensions(template.width, template.height);

        setCanvasDimensions({ width, height });
        setCanvasScale(scale);

        // If canvas is already initialized, we need to resize it
        if (fabricCanvasRef.current) {
          fabricCanvasRef.current.setWidth(width);
          fabricCanvasRef.current.setHeight(height);

          // Also need to rescale any background image
          if (fabricCanvasRef.current.backgroundImage) {
            const bgImg = fabricCanvasRef.current.backgroundImage as fabric.Image;
            bgImg.scaleToWidth(width);
            fabricCanvasRef.current.renderAll();
          }

          // Update text elements on canvas
          updateCanvasFromTextElements();
        }
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [template, calculateCanvasDimensions, textElements]);

  // Initialize Fabric.js canvas
  useEffect(() => {
    if (template && canvasRef.current && !fabricCanvasRef.current && canvasDimensions.width > 0) {
      const canvas = new fabric.Canvas(canvasRef.current, {
        width: canvasDimensions.width,
        height: canvasDimensions.height,
        backgroundColor: "#000000",
      });

      fabricCanvasRef.current = canvas;

      // Load template image using Image API
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = function () {
        const fabricImage = new fabric.Image(img);

        // Scale to fit canvas dimensions
        fabricImage.scaleToWidth(canvasDimensions.width);

        // Manually set background image
        canvas.backgroundImage = fabricImage;
        canvas.renderAll();
      };
      img.onerror = function (err) {
        console.error("Error loading image:", err);
      };
      img.src = template.imageUrl;

      // Set up object selection event
      canvas.on("selection:created", (e: any) => {
        const selected = e.selected && e.selected[0];
        if (selected && selected.data?.id) {
          setSelectedTextId(selected.data.id);
        }
      });

      canvas.on("selection:updated", (e: any) => {
        const selected = e.selected && e.selected[0];
        if (selected && selected.data?.id) {
          setSelectedTextId(selected.data.id);
        }
      });

      canvas.on("selection:cleared", () => {
        setSelectedTextId(null);
      });

      // Text object modified event
      canvas.on("object:modified", (e: any) => {
        const target = e.target;
        if (target && target.data?.id) {
          // Type assertion with any to bypass TypeScript restrictions
          const textObj = target as any;
          const id = textObj.data.id;

          updateTextElementFromCanvas(id, textObj);
        }
      });

      setCanvasReady(true);
    }

    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
    };
  }, [template, canvasDimensions]);

  // Add default text when canvas is ready
  useEffect(() => {
    if (canvasReady && fabricCanvasRef.current && textElements.length === 0) {
      addNewTextElement();
    }
  }, [canvasReady, textElements.length]);

  // Update canvas when text elements change
  useEffect(() => {
    if (fabricCanvasRef.current && canvasReady) {
      updateCanvasFromTextElements();
    }
  }, [textElements, canvasReady]);

  const updateCanvasFromTextElements = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Remove all text objects
    const textObjects = canvas.getObjects().filter((obj) => obj.type === "textbox");
    textObjects.forEach((obj) => canvas.remove(obj));

    // Add updated text elements
    textElements.forEach((el) => {
      // Calculate scaled dimensions
      const scaledLeft = el.x * canvasScale;
      const scaledTop = el.y * canvasScale;
      const scaledFontSize = el.fontSize * canvasScale;
      const scaledWidth = (el.width || 200) * canvasScale;
      const scaledStrokeWidth = el.strokeWidth * canvasScale;

      // Use Textbox instead of IText for multiline support
      const textObj = new fabric.Textbox(el.text, {
        left: scaledLeft,
        top: scaledTop,
        fontFamily: el.fontFamily,
        fontSize: scaledFontSize,
        fill: el.color,
        textAlign: el.textAlign,
        stroke: el.strokeColor,
        strokeWidth: scaledStrokeWidth,
        width: scaledWidth,
        splitByGrapheme: false, // Split by words not characters
        lockUniScaling: false, // Allow independent scaling
        scaleX: el.scaleX || 1,
        scaleY: el.scaleY || 1,
        angle: el.angle || 0,
        shadow: new fabric.Shadow({
          color: "rgba(0,0,0,0.6)",
          offsetX: 2 * canvasScale,
          offsetY: 2 * canvasScale,
          blur: 3 * canvasScale,
        }),
      });

      // Add data property using Object.assign to bypass TypeScript restrictions
      Object.assign(textObj, { data: { id: el.id } });

      canvas.add(textObj);
    });

    // Select previously selected object if exists
    if (selectedTextId) {
      const selectedObj = canvas.getObjects().find((obj) => {
        // Use type assertion to access data property
        const objAny = obj as any;
        return objAny.data && objAny.data.id === selectedTextId;
      });

      if (selectedObj) {
        canvas.setActiveObject(selectedObj);
      }
    }

    canvas.renderAll();
  };

  const updateTextElementFromCanvas = (id: string, textObj: any) => {
    setTextElements((prev) =>
      prev.map((el) => {
        if (el.id === id) {
          // Convert scaled positions back to original coordinates
          const originalX = textObj.left / canvasScale;
          const originalY = textObj.top / canvasScale;
          const originalWidth = textObj.width / canvasScale;

          return {
            ...el,
            text: textObj.text || "",
            // Store unscaled values for export
            x: originalX,
            y: originalY,
            width: originalWidth,
            scaleX: textObj.scaleX || 1,
            scaleY: textObj.scaleY || 1,
            angle: textObj.angle || 0,
          };
        }
        return el;
      }),
    );
  };

  const addNewTextElement = () => {
    const id = `text-${Date.now()}`;
    // Calculate center in original template coordinates
    const centerX = template ? template.width / 2 : 200;
    const centerY = template ? template.height / 2 : 200;

    const newElement: TextElement = {
      id,
      text: "ADD TEXT HERE",
      x: centerX - 100,
      y: centerY,
      fontSize: 36,
      color: "#ffffff",
      textAlign: "center",
      fontFamily: "Impact",
      strokeWidth: 1,
      strokeColor: "#000000",
      scaleX: 1,
      scaleY: 1,
      width: 200,
      angle: 0,
    };

    setTextElements((prev) => [...prev, newElement]);
    setSelectedTextId(id);
  };

  const deleteTextElement = (id: string) => {
    setTextElements((prev) => prev.filter((el) => el.id !== id));
    if (selectedTextId === id) {
      setSelectedTextId(null);
    }
  };

  const updateTextElement = (id: string, updates: Partial<TextElement>) => {
    setTextElements((prev) =>
      prev.map((el) => {
        if (el.id === id) {
          return { ...el, ...updates };
        }
        return el;
      }),
    );
  };

  const saveMeme = async () => {
    if (!fabricCanvasRef.current || !template) return;

    try {
      // If high quality export is enabled, we use a different approach
      if (highQualityExport && canvasScale !== 1) {
        // Create a temporary canvas at the original template size
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = template.width;
        tempCanvas.height = template.height;

        // Create a temporary Fabric canvas for high-quality export
        const exportCanvas = new fabric.Canvas(tempCanvas, {
          width: template.width,
          height: template.height,
          backgroundColor: "#000000",
        });

        // Load the template image
        const img = new Image();
        img.crossOrigin = "anonymous";

        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            // Add background image at full size
            const fabricImage = new fabric.Image(img);
            fabricImage.scaleToWidth(template.width);
            exportCanvas.backgroundImage = fabricImage;

            // Add all text elements at full size
            textElements.forEach((el) => {
              const textObj = new fabric.Textbox(el.text, {
                left: el.x,
                top: el.y,
                fontFamily: el.fontFamily,
                fontSize: el.fontSize,
                fill: el.color,
                textAlign: el.textAlign,
                stroke: el.strokeColor,
                strokeWidth: el.strokeWidth,
                width: el.width || 200,
                splitByGrapheme: false,
                lockUniScaling: false,
                scaleX: el.scaleX || 1,
                scaleY: el.scaleY || 1,
                angle: el.angle || 0,
                shadow: new fabric.Shadow({
                  color: "rgba(0,0,0,0.6)",
                  offsetX: 2,
                  offsetY: 2,
                  blur: 3,
                }),
              });

              exportCanvas.add(textObj);
            });

            exportCanvas.renderAll();
            resolve();
          };
          img.onerror = () => reject(new Error("Failed to load image"));
          img.src = template.imageUrl;
        });

        // Get data URL from the export canvas
        const dataUrl = exportCanvas.toDataURL({
          format: "png",
          quality: 1,
          multiplier: 1,
        });

        // Clean up the temporary canvas
        exportCanvas.dispose();

        // Download the image
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `meme-${template.id}-hq-${new Date().getTime()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Simple approach: just get what's displayed on screen
        const dataUrl = fabricCanvasRef.current.toDataURL({
          format: "png",
          quality: 1,
          multiplier: 1, // Use what you see approach
        });

        // Download the image
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `meme-${template.id}-${new Date().getTime()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error saving meme:", error);
      alert("Failed to save meme. Please try again.");
    }
  };

  const selectedElement = selectedTextId
    ? textElements.find((el) => el.id === selectedTextId)
    : null;

  if (loading || !template) {
    return (
      <Box className="flex justify-center items-center h-64">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Box className="mb-4 flex items-center">
        <IconButton onClick={() => navigate("/")} className="mr-2">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          Editing: {template.name}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper className="bg-gray-800 p-4 mb-4 flex justify-center overflow-hidden">
            <div className="canvas-container relative">
              <canvas ref={canvasRef} />

              {/* Display information about canvas scaling */}
              <Typography variant="caption" className="text-gray-400 block mt-2 text-center">
                Original size: {template.width}x{template.height}px
                {canvasScale !== 1 && ` • Scaled to ${Math.round(canvasScale * 100)}%`}• Display
                size: {canvasDimensions.width}x{canvasDimensions.height}px
              </Typography>
            </div>
          </Paper>
          <Box className="flex justify-end items-center gap-2 mb-4">
            {canvasScale !== 1 && (
              <FormControlLabel
                control={
                  <Switch
                    checked={highQualityExport}
                    onChange={(e) => setHighQualityExport(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" className="text-white">
                    High Quality
                  </Typography>
                }
              />
            )}
            <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={saveMeme}>
              Save Meme
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper className="bg-gray-800 p-4 max-h-[800px] overflow-y-auto">
            <Box className="flex justify-between items-center mb-3">
              <Typography variant="h6">Text Elements</Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={addNewTextElement}
              >
                Add Text
              </Button>
            </Box>

            {textElements.length === 0 ? (
              <Typography className="text-gray-400 mb-3">Add text elements to your meme</Typography>
            ) : (
              <div className="space-y-3">
                {textElements.map((el) => (
                  <Accordion
                    key={el.id}
                    expanded={selectedTextId === el.id}
                    onChange={() => setSelectedTextId(selectedTextId === el.id ? null : el.id)}
                    className="bg-gray-700"
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className={selectedTextId === el.id ? "bg-indigo-800" : ""}
                    >
                      <Typography className="truncate w-64">{el.text || "Empty text"}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div className="space-y-3">
                        {/* Replace TextField with multiline TextArea */}
                        <TextField
                          fullWidth
                          label="Text"
                          variant="outlined"
                          value={el.text}
                          onChange={(e) => updateTextElement(el.id, { text: e.target.value })}
                          className="bg-gray-800"
                          size="small"
                          multiline
                          rows={4}
                        />

                        <div>
                          <Typography gutterBottom>Font Size</Typography>
                          <Slider
                            value={el.fontSize}
                            min={10}
                            max={100}
                            step={1}
                            onChange={(_, value) =>
                              updateTextElement(el.id, { fontSize: value as number })
                            }
                            valueLabelDisplay="auto"
                            className="text-indigo-500"
                          />
                        </div>

                        <div>
                          <Typography gutterBottom>Stroke Width</Typography>
                          <Slider
                            value={el.strokeWidth}
                            min={0}
                            max={5}
                            step={0.1}
                            onChange={(_, value) =>
                              updateTextElement(el.id, { strokeWidth: value as number })
                            }
                            valueLabelDisplay="auto"
                            className="text-indigo-500"
                          />
                        </div>

                        <FormControl fullWidth size="small">
                          <InputLabel>Text Align</InputLabel>
                          <Select
                            value={el.textAlign}
                            label="Text Align"
                            onChange={(e) =>
                              updateTextElement(el.id, {
                                textAlign: e.target.value as "left" | "center" | "right",
                              })
                            }
                            className="bg-gray-800"
                          >
                            <MenuItem value="left">Left</MenuItem>
                            <MenuItem value="center">Center</MenuItem>
                            <MenuItem value="right">Right</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                          <InputLabel>Font Family</InputLabel>
                          <Select
                            value={el.fontFamily}
                            label="Font Family"
                            onChange={(e) =>
                              updateTextElement(el.id, { fontFamily: e.target.value as string })
                            }
                            className="bg-gray-800"
                          >
                            <MenuItem value="Impact">Impact (Default)</MenuItem>
                            <MenuItem value="Arial">Arial</MenuItem>
                            <MenuItem value="Comic Sans MS">Comic Sans</MenuItem>
                            <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                          </Select>
                        </FormControl>

                        <div className="flex items-center justify-between mt-2">
                          <div>
                            <Typography gutterBottom>Text Color</Typography>
                            <input
                              type="color"
                              value={el.color}
                              onChange={(e) => updateTextElement(el.id, { color: e.target.value })}
                              className="w-12 h-8"
                            />
                          </div>
                          <div>
                            <Typography gutterBottom>Stroke Color</Typography>
                            <input
                              type="color"
                              value={el.strokeColor}
                              onChange={(e) =>
                                updateTextElement(el.id, { strokeColor: e.target.value })
                              }
                              className="w-12 h-8"
                            />
                          </div>
                        </div>

                        <Box className="flex justify-end mt-2">
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => deleteTextElement(el.id)}
                            size="small"
                          >
                            Delete
                          </Button>
                        </Box>
                      </div>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default MemeEditor;
